import { VStack, Text, Link, Separator } from '@chakra-ui/react';
import type { WorkplaceMarker } from '../../types/workplaceOverlay'
import { ContactStatus } from '../../types/turfMap'

interface WorkplacePopupProps {
  workplace: WorkplaceMarker
  onContactClick?: (contactId: string) => void
  onWorksiteClick?: (worksiteId: string) => void
}

export function WorkplacePopup({ workplace, onContactClick, onWorksiteClick }: WorkplacePopupProps) {
  const addressLine = [workplace.city, workplace.state].filter(Boolean).join(', ')

  return (
    <VStack align="stretch" gap={1} minW="220px">
      {/* Worksite name — clickable */}
      <Link
        fontSize="sm"
        fontWeight="bold"
        color="accent"
        _hover={{ textDecoration: 'underline' }}
        onClick={(e) => {
          e.stopPropagation()
          onWorksiteClick?.(workplace.worksiteId)
        }}
      >
        {workplace.worksiteLabel}
        {workplace.businessName && (
          <Text as="span" fontWeight="normal" color="gray.500">
            {' '}({workplace.businessName})
          </Text>
        )}
      </Link>

      {/* Address */}
      <Text fontSize="xs" color="gray.600">
        {workplace.streetAddress}
      </Text>
      {(addressLine || workplace.zip) && (
        <Text fontSize="xs" color="gray.600">
          {addressLine} {workplace.zip}
        </Text>
      )}

      {/* Stats */}
      <Text fontSize="xs" color="gray.500" mt={1}>
        {workplace.employeeCount} {workplace.employeeCount === 1 ? 'employee' : 'employees'} •{' '}
        {workplace.visitedCount - workplace.needsRevisitCount} reached
        {workplace.needsRevisitCount > 0 && (
          <Text as="span" color="orange.500"> • {workplace.needsRevisitCount} need revisit</Text>
        )}
      </Text>

      {/* Employee list */}
      {workplace.employees.length > 0 && (
        <>
          <Separator my={1} />
          <VStack align="stretch" gap={0} fontSize="xs">
            {workplace.employees.map(emp => (
              <Link
                key={emp.contactId}
                color="accent"
                _hover={{ textDecoration: 'underline' }}
                onClick={(e) => {
                  e.stopPropagation()
                  onContactClick?.(emp.contactId)
                }}
              >
                • {emp.contactName}
                {emp.position && <Text as="span" color="gray.600">, {emp.position}</Text>}
                {emp.unionName && (
                  <Text as="span" color="gray.500">
                    {' '}({emp.unionName}{emp.unionLocalNumber ? ` Local ${emp.unionLocalNumber}` : ''})
                  </Text>
                )}
                {emp.isVisited && (
                  (emp.contactStatus === ContactStatus.NoAnswer || emp.contactStatus === ContactStatus.Callback)
                    ? ' ↩'
                    : ' ✓'
                )}
              </Link>
            ))}
          </VStack>
        </>
      )}
    </VStack>
  );
}
